import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { AuthProvider as OidcProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import type { AuthProviderProps } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import type { User } from 'oidc-client-ts';
import { env } from './env';
import { sessionStore } from './session-store';

/**
 * Normalised session, so nothing above this file cares whether we are running against real
 * Cognito or the local dev bypass.
 */
export interface Session {
  status: 'loading' | 'authenticated' | 'anonymous' | 'error';
  idToken: string | null;
  /** Claims-derived display value; the authoritative profile comes from GET /api/v1/me. */
  email: string | null;
  error: Error | null;
  /** True when the fake local user is in play. Surfaced in the UI so it is never a surprise. */
  bypass: boolean;
  signIn: () => void;
  signOut: () => void;
  clearError: () => void;
}

const SessionContext = createContext<Session | null>(null);

/**
 * Renew this far ahead of expiry when the tab comes back to the foreground. Longer than the
 * 5-minute notification window below so a tab that wakes up mid-window still refreshes rather
 * than racing the first request out with a nearly-dead token.
 */
const RENEW_WHEN_REMAINING_SECONDS = 10 * 60;

/**
 * True when this user can be refreshed without bouncing through Cognito's hosted UI.
 *
 * `expired` is false-y only while the access token still has life in it; oidc-client-ts keeps
 * the stored user (and its refresh token) around either way, which is exactly what makes the
 * silent renew below possible after a cold load.
 */
function shouldRenew(user: User | null | undefined): boolean {
  if (!user?.refresh_token) return false;
  if (user.expired) return true;
  return (user.expires_in ?? 0) <= RENEW_WHEN_REMAINING_SECONDS;
}

/* ------------------------------------------------------------------ real Cognito ---- */

const oidcConfig: AuthProviderProps = {
  authority: env.cognitoAuthority,
  client_id: env.cognitoClientId,
  redirect_uri: env.redirectUri,
  post_logout_redirect_uri: env.logoutUri,
  // Authorization Code + PKCE. oidc-client-ts adds the code_challenge automatically for
  // response_type=code on a public client.
  response_type: 'code',
  scope: 'openid email profile',
  loadUserInfo: false,
  automaticSilentRenew: true,
  // Start renewing 5 minutes out instead of the 60-second default. Cognito id tokens live 60
  // minutes, so this costs one extra token call per session at most and leaves room for a slow
  // network or a briefly-suspended timer.
  accessTokenExpiringNotificationTimeInSeconds: 5 * 60,
  monitorSession: false,
  userStore:
    typeof window === 'undefined'
      ? undefined
      : new WebStorageStateStore({ store: window.localStorage }),
  // Drop ?code=&state= from the address bar once the exchange has happened.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

function OidcSessionBridge({ children }: { children: ReactNode }) {
  const auth = useOidcAuth();
  const [signOutRequested, setSignOutRequested] = useState(false);
  const [renewFailed, setRenewFailed] = useState(false);
  /** The in-flight renewal, so concurrent 401s and focus events share one token call. */
  const renewal = useRef<Promise<boolean> | null>(null);

  const idToken = auth.user?.id_token ?? null;

  /**
   * Trades the stored refresh token for a fresh id token.
   *
   * `signinSilent` uses the refresh_token grant directly whenever the stored user has one — no
   * hidden iframe, so this is unaffected by third-party cookie blocking. The new token is
   * published to the session store here rather than waiting for the re-render, because the
   * caller (the 401 path) replays its request the moment this resolves.
   */
  const renew = useCallback((): Promise<boolean> => {
    renewal.current ??= (async () => {
      try {
        const user = await auth.signinSilent();
        const token = user?.id_token ?? null;
        sessionStore.setIdToken(token);
        setRenewFailed(!token);
        return Boolean(token);
      } catch {
        setRenewFailed(true);
        return false;
      } finally {
        renewal.current = null;
      }
    })();
    return renewal.current;
  }, [auth]);

  /**
   * Restores the session on a cold load.
   *
   * Without this, coming back to the tab after the 60-minute id token has lapsed shows the
   * sign-in screen even though a valid 90-day refresh token is sitting in localStorage:
   * react-oidc-context reports `isAuthenticated: !user.expired`, and `automaticSilentRenew`
   * cannot help because oidc-client-ts cancels the expiring timer for a user that is *already*
   * past expiry. Derived during render, not in an effect, so `status` below never flickers
   * through 'anonymous' and RequireAuth never flashes the sign-in card.
   */
  const restoring =
    !auth.isLoading && !auth.isAuthenticated && !renewFailed && !signOutRequested
      ? Boolean(auth.user?.refresh_token)
      : false;

  useEffect(() => {
    if (restoring) void renew();
  }, [restoring, renew]);

  // A laptop that sleeps through the expiry window never fires the renewal timer, so re-check
  // whenever the tab comes back or the network returns.
  useEffect(() => {
    const renewIfStale = () => {
      if (document.visibilityState !== 'visible') return;
      if (signOutRequested || !shouldRenew(auth.user)) return;
      void renew();
    };
    document.addEventListener('visibilitychange', renewIfStale);
    window.addEventListener('focus', renewIfStale);
    window.addEventListener('online', renewIfStale);
    return () => {
      document.removeEventListener('visibilitychange', renewIfStale);
      window.removeEventListener('focus', renewIfStale);
      window.removeEventListener('online', renewIfStale);
    };
  }, [auth.user, renew, signOutRequested]);

  // Published during render, NOT in an effect. React flushes effects child-first, so the
  // `/me` query inside RequireAuth fires before any effect here could run — it would read a
  // null token, get rewritten to the anonymous `/api/public/v1` mirror by resolvePath, and
  // the session would look signed-out on every refresh. Assignment is idempotent, so the
  // StrictMode double-render is harmless.
  //
  // Gated on `isAuthenticated` so an expired token is never published: `renew` writes the fresh
  // one straight to the store, and an ungated assignment here would let a later render put the
  // stale token back and spend a request proving it is dead.
  sessionStore.setIdToken(auth.isAuthenticated ? idToken : null);

  const signIn = useCallback(() => {
    // Cognito federates straight to Google, skipping the Hosted UI provider picker.
    void auth.signinRedirect({ extraQueryParams: { identity_provider: 'Google' } });
  }, [auth]);

  const signOut = useCallback(() => {
    setSignOutRequested(true);
    sessionStore.setIdToken(null);
    void auth
      .removeUser()
      .catch(() => undefined)
      .finally(() => {
        // Cognito's logout endpoint is not part of the OIDC discovery document, so it has to
        // be built by hand from the Hosted UI domain.
        if (!env.cognitoDomain || !env.cognitoClientId) {
          window.location.assign(env.logoutUri);
          return;
        }
        const url = new URL(`${env.cognitoDomain}/logout`);
        url.searchParams.set('client_id', env.cognitoClientId);
        url.searchParams.set('logout_uri', env.logoutUri);
        window.location.assign(url.toString());
      });
  }, [auth]);

  /**
   * A 401 means the API rejected the token we sent. That is nearly always an expired id token
   * rather than a dead session, so try the refresh token first — the old behaviour called
   * `removeUser()` immediately, which threw away the refresh token and turned one badly-timed
   * request into a full sign-out. Only when the renewal itself fails is the session really over.
   */
  useEffect(() => {
    sessionStore.setUnauthorizedHandler(async () => {
      if (auth.user?.refresh_token && !signOutRequested) {
        if (await renew()) return true;
      }
      sessionStore.setIdToken(null);
      await auth.removeUser().catch(() => undefined);
      return false;
    });
    return () => sessionStore.setUnauthorizedHandler(null);
  }, [auth, renew, signOutRequested]);

  // A renewal that fails in the background is not a sign-in failure — the refresh token simply
  // ran out its 90 days, or the network was down. Surfacing it as 'error' would greet the user
  // with "Sign-in failed: …" on a screen they never asked for, so it degrades to the ordinary
  // sign-in card instead. Only errors from an interaction the user started are shown.
  const fatalError =
    auth.error && auth.error.source !== 'renewSilent' && auth.error.source !== 'signinSilent'
      ? auth.error
      : null;

  const status: Session['status'] =
    auth.isLoading || restoring
      ? 'loading'
      : fatalError && !signOutRequested
        ? 'error'
        : auth.isAuthenticated && idToken
          ? 'authenticated'
          : 'anonymous';

  const value = useMemo<Session>(
    () => ({
      status,
      idToken,
      email: typeof auth.user?.profile.email === 'string' ? auth.user.profile.email : null,
      error: fatalError,
      bypass: false,
      signIn,
      signOut,
      clearError: () => auth.clearStaleState().catch(() => undefined),
    }),
    [status, idToken, auth, fatalError, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/* -------------------------------------------------------------------- dev bypass ---- */

const BYPASS_TOKEN = 'local-dev-bypass-token';

/**
 * Dev-only escape hatch so the dashboard can be worked on without a Cognito pool. It is
 * unreachable in a production bundle: `env.authBypass` is `import.meta.env.DEV && flag`, and
 * Vite statically replaces DEV with `false` in `vite build`, so this branch is dead code that
 * gets tree-shaken away.
 */
function BypassSessionProvider({ children }: { children: ReactNode }) {
  const [signedOut, setSignedOut] = useState(false);

  useEffect(() => {
    sessionStore.setIdToken(signedOut ? null : BYPASS_TOKEN);
    // There is nothing to renew against a fake token, so a 401 is always terminal here.
    sessionStore.setUnauthorizedHandler(() => {
      setSignedOut(true);
      return false;
    });
    return () => sessionStore.setUnauthorizedHandler(null);
  }, [signedOut]);

  const value = useMemo<Session>(
    () => ({
      status: signedOut ? 'anonymous' : 'authenticated',
      idToken: signedOut ? null : BYPASS_TOKEN,
      email: 'local-admin@seez.tech',
      error: null,
      bypass: true,
      signIn: () => setSignedOut(false),
      signOut: () => setSignedOut(true),
      clearError: () => undefined,
    }),
    [signedOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/* ------------------------------------------------------------------------ public ---- */

export function AuthProvider({ children }: { children: ReactNode }) {
  // `import.meta.env.DEV` is literally replaced with `false` by `vite build`, so the whole
  // branch — and BypassSessionProvider with it — is tree-shaken out of production. Verified
  // by grepping the built bundle for the bypass token.
  if (import.meta.env.DEV && env.authBypass) {
    return <BypassSessionProvider>{children}</BypassSessionProvider>;
  }
  return (
    <OidcProvider {...oidcConfig}>
      <OidcSessionBridge>{children}</OidcSessionBridge>
    </OidcProvider>
  );
}

export function useSession(): Session {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <AuthProvider>');
  return context;
}
